// The audio wiring for a single-player match: the recent-combat latch for the
// music director, the biome under the camera focus, and the match-critical SFX
// preload. Extracted from App.tsx (ROADMAP 7.14); the hook order is unchanged.

import { useEffect, useMemo } from 'react';
import { isCameraOverCity, sampleBiomeAtWorld } from '../audio/biomeLookup.js';
import { getMatchCriticalSoundIds, preloadCriticalSfx } from '../audio/preload.js';
import { useAudioDirector } from '../audio/useSounds.js';
import type { GameMode } from '../multiplayer/lobby/shouldConnectMpLobby.js';
import type { Vec3 } from '../render/camera/flight.js';
import type { SimWorld } from '../sim/useSimWorld/index.js';
import { useRecentCombat } from '../useRecentCombat.js';

export function useGameAudio(
  sim: SimWorld,
  gameMode: GameMode,
  cameraTarget: Vec3 | null | undefined,
  localPlayerIndex: number,
) {
  // Recent-combat flag for the music director. Latches true for 5s after any
  // unit:combat / city:damaged / city:captured event, then falls back to peace.
  // Note: because this is a ref, the music director re-derives only on the next
  // sim tick (which re-renders App) — there is up to one tick of lag before the
  // combat -> peace transition. Acceptable per the plan.
  const recentCombatRef = useRecentCombat(sim.events);

  // Sample the biome under the camera focus so the ambient bed matches what
  // the player is actually looking at. Returns null when the camera hasn't
  // been explicitly aimed anywhere.
  const cameraFocusBiome = useMemo(() => {
    if (!sim.snapshot || !cameraTarget) return null;
    return sampleBiomeAtWorld(sim.snapshot, cameraTarget);
  }, [sim.snapshot, cameraTarget]);

  useAudioDirector({
    musicMode:
      gameMode === 'menu' || gameMode === 'setup'
        ? 'menu'
        : sim.victory === null
          ? recentCombatRef.current
            ? 'combat'
            : 'single'
          : sim.victory.winner === localPlayerIndex
            ? 'victory'
            : 'defeat',
    recentCombat: recentCombatRef.current,
    doctrine: sim.localPlayer?.dominant ?? null,
    cameraBiome: cameraFocusBiome,
    cameraOverCity: sim.snapshot
      ? isCameraOverCity(sim.snapshot, cameraTarget ?? [0, 0, 0])
      : false,
  });

  // Preload match-critical SFX so first-play is hitch-free. Fires on entry into
  // single or multiplayer-match mode.
  useEffect(() => {
    if (gameMode !== 'single' && gameMode !== 'multiplayer-match') return;
    void preloadCriticalSfx(getMatchCriticalSoundIds());
  }, [gameMode]);
}
